import React, { useEffect, useMemo, useRef, useState } from 'react';
import MdiIcon from '@mdi/react';
import { mdiMagnify } from '@mdi/js';
import { OUNode, TreeNode } from '../../types';
import { buildTree } from '../../utils/treeUtils';
import { Icon } from './icons';

interface Props {
  value: string | null;
  nodes: OUNode[];
  onChange: (parentId: string | null) => void;
}

interface FlatOption {
  id: string | null;
  name: string;
  type: 'OrganizationalUnit' | 'Location' | '__global__';
  depth: number;
}

function flattenForPicker(nodes: TreeNode[], depth = 0): FlatOption[] {
  const result: FlatOption[] = [];
  for (const n of nodes) {
    if (n.type !== 'OrganizationalUnit') continue;
    result.push({ id: n.id, name: n.name, type: 'OrganizationalUnit', depth });
    result.push(...flattenForPicker(n.children, depth + 1));
  }
  return result;
}

const ParentPicker: React.FC<Props> = ({ value, nodes, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', handler);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const ouNodes = useMemo(
    () => nodes.filter(n => n.id !== '__global__'),
    [nodes],
  );

  const tree = useMemo(() => buildTree(ouNodes, []), [ouNodes]);

  const allOptions = useMemo<FlatOption[]>(() => [
    { id: null, name: 'Global / Platform', type: '__global__', depth: 0 },
    ...flattenForPicker(tree, 1),
  ], [tree]);

  const filtered = useMemo(() => {
    if (!search) return allOptions;
    const q = search.toLowerCase();
    return allOptions.filter(o => o.name.toLowerCase().includes(q));
  }, [allOptions, search]);

  const selectedNode = value ? nodes.find(n => n.id === value) : null;
  const label = selectedNode ? selectedNode.name : 'Global / Platform';
  const triggerIcon = selectedNode
    ? (selectedNode.type === 'Location' ? Icon.location : Icon.building)
    : Icon.globe;

  return (
    <div className="picker-wrap picker-wrap--form picker-wrap--parent" ref={ref}>
      <button className="picker-trigger" onClick={() => setOpen(v => !v)}>
        {triggerIcon}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, textAlign: 'left' }}>
          {label}
        </span>
        {Icon.chevronDown}
      </button>
      {open && (
        <div className="picker-dropdown picker-dropdown--parent">
          <div className="picker-search">
            <MdiIcon path={mdiMagnify} size={0.7} color="var(--color-dark-l3)" />
            <input
              ref={searchRef}
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="picker-list">
            {filtered.map(opt => (
              <div
                key={opt.id ?? '__global__'}
                className={`picker-option ${value === opt.id ? 'selected' : ''}`}
                style={{ paddingLeft: `calc(var(--space-4) + ${opt.depth * 16}px)` }}
                onClick={() => { onChange(opt.id); setOpen(false); setSearch(''); }}
              >
                <span className="name-icon">
                  {opt.type === '__global__' ? Icon.globe : Icon.building}
                </span>
                <span style={{ flex: 1 }}>{opt.name}</span>
                {value === opt.id && <span className="check-icon">{Icon.check}</span>}
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: '10px var(--space-4)', fontSize: 'var(--font-size-sm)', color: 'var(--color-dark-l3)' }}>
                No results
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentPicker;
